import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { useAuthCleanup } from './AuthCleanupContext';
import {
  DEFAULT_DONATION_CONFIG,
  type DonationConfig,
  type DonationTrigger,
  type UserEngagementMetrics,
  type DonationPromptRecord,
  type DonationContextValue,
  type ActiveDonationPrompt,
  type DonationClickAction,
  type DonationBlock,
} from '@/types/donation';
import type { UserStats } from '@/types';
import {
  subscribeToDonationConfig,
  subscribeToPromptHistory,
  recordPromptShown,
  recordPromptAction,
  evaluateTriggers,
  substituteMessageVariables,
} from '@/services/donationTriggers';
import {
  subscribeToEngagementMetrics,
  startSession,
  endSession,
  updateSessionActivity,
} from '@/services/engagementMetrics';
import { getDonationTemplate } from '@/services/donationTemplates';

// ============================================
// CONTEXT
// ============================================

const DonationContext = createContext<DonationContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface DonationProviderProps {
  children: React.ReactNode;
}

export function DonationProvider({ children }: DonationProviderProps) {
  const { authState } = useAuth();
  const { registerCleanup } = useAuthCleanup();
  const isAuthenticated = authState.status === 'authenticated';
  const userId = isAuthenticated ? authState.profile.uid : null;

  // State
  const [config, setConfig] = useState<DonationConfig>(DEFAULT_DONATION_CONFIG as DonationConfig);
  const [engagementMetrics, setEngagementMetrics] = useState<UserEngagementMetrics | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [promptHistory, setPromptHistory] = useState<DonationPromptRecord[]>([]);
  const [activePrompt, setActivePrompt] = useState<ActiveDonationPrompt | null>(null);
  const [activePromptRecord, setActivePromptRecord] = useState<DonationPromptRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refs for session tracking
  const sessionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastEvaluationRef = useRef<string | null>(null);
  // Track dismissed prompts locally to prevent re-showing before Firebase updates
  const dismissedPromptIdsRef = useRef<Set<string>>(new Set());
  // Track pending evaluation to prevent race conditions
  const pendingEvaluationRef = useRef<string | null>(null);

  // Refs for unsubscribe functions
  const unsubscribeConfigRef = useRef<(() => void) | null>(null);
  const unsubscribeMetricsRef = useRef<(() => void) | null>(null);
  const unsubscribeHistoryRef = useRef<(() => void) | null>(null);

  // Register cleanup function for auth logout
  useEffect(() => {
    registerCleanup('donation-context', () => {
      // Unsubscribe from listeners
      unsubscribeConfigRef.current?.();
      unsubscribeConfigRef.current = null;
      unsubscribeMetricsRef.current?.();
      unsubscribeMetricsRef.current = null;
      unsubscribeHistoryRef.current?.();
      unsubscribeHistoryRef.current = null;

      // Clear intervals
      if (sessionIntervalRef.current) {
        clearInterval(sessionIntervalRef.current);
        sessionIntervalRef.current = null;
      }

      // Reset state
      setConfig(DEFAULT_DONATION_CONFIG as DonationConfig);
      setEngagementMetrics(null);
      setUserStats(null);
      setPromptHistory([]);
      setActivePrompt(null);
      setActivePromptRecord(null);
      setIsLoading(false);
      setResolvedTemplateBlocks(null);
      lastEvaluationRef.current = null;
      dismissedPromptIdsRef.current.clear();
      pendingEvaluationRef.current = null;
    });
  }, [registerCleanup]);

  // Subscribe to donation config
  useEffect(() => {
    const unsub = subscribeToDonationConfig((newConfig) => {
      setConfig(newConfig);
    });
    unsubscribeConfigRef.current = unsub;

    return () => unsub();
  }, []);

  // Subscribe to user's engagement metrics
  useEffect(() => {
    if (!userId) {
      setEngagementMetrics(null);
      setIsLoading(false);
      return;
    }

    const unsub = subscribeToEngagementMetrics(userId, (metrics) => {
      setEngagementMetrics(metrics);
      setIsLoading(false);
    });
    unsubscribeMetricsRef.current = unsub;

    return () => unsub();
  }, [userId]);

  // Subscribe to prompt history
  useEffect(() => {
    if (!userId) {
      setPromptHistory([]);
      return;
    }

    const unsub = subscribeToPromptHistory(userId, (history) => {
      setPromptHistory(history);

      // Clean up dismissed IDs that Firebase has now confirmed
      // (prompts that now have an action set)
      const confirmedDismissals = history
        .filter(
          (p): p is DonationPromptRecord & { id: string } =>
            p.action !== null && !!p.id && dismissedPromptIdsRef.current.has(p.id)
        )
        .map((p) => p.id);

      confirmedDismissals.forEach((id) => {
        dismissedPromptIdsRef.current.delete(id);
      });
    });
    unsubscribeHistoryRef.current = unsub;

    return () => unsub();
  }, [userId]);

  // Load user stats from profile
  useEffect(() => {
    setUserStats(null);
  }, [isAuthenticated, authState]);

  // Session tracking
  useEffect(() => {
    if (!userId) return;

    // Start session when component mounts
    startSession(userId).catch(console.error);

    // Update session activity every 5 minutes
    sessionIntervalRef.current = setInterval(
      () => {
        updateSessionActivity(userId).catch(console.error);
      },
      5 * 60 * 1000
    );

    // Handle visibility change for session tracking
    const handleVisibilityChange = () => {
      if (document.hidden) {
        endSession(userId).catch(console.error);
      } else {
        startSession(userId).catch(console.error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (sessionIntervalRef.current) {
        clearInterval(sessionIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      endSession(userId).catch(console.error);
    };
  }, [userId]);

  // Check for pending manual prompts first
  useEffect(() => {
    if (!userId || isLoading || activePrompt) {
      return;
    }

    // Find pending manual prompts (action: null, isManual: true)
    // Exclude prompts we've already dismissed locally (before Firebase updates)
    const pendingManualPrompt = promptHistory.find(
      (p) =>
        p.action === null &&
        p.isManual === true &&
        p.message &&
        p.id &&
        !dismissedPromptIdsRef.current.has(p.id)
    );

    if (pendingManualPrompt && pendingManualPrompt.id) {
      // Create a trigger from the manual prompt data
      const manualTrigger: DonationTrigger = {
        id: pendingManualPrompt.triggerId,
        name: pendingManualPrompt.triggerName,
        enabled: true,
        priority: 100,
        conditions: [],
        conditionLogic: 'AND',
        message: pendingManualPrompt.message || '',
        icon: pendingManualPrompt.icon || 'heart',
        // Include click action from prompt record
        clickAction: pendingManualPrompt.clickAction,
      };

      setActivePrompt({
        trigger: manualTrigger,
        promptId: pendingManualPrompt.id,
      });
      // Store the prompt record for accessing clickAction override
      setActivePromptRecord(pendingManualPrompt);
    }
  }, [userId, promptHistory, isLoading, activePrompt]);

  // Evaluate automatic triggers when metrics/config change
  useEffect(() => {
    let isMounted = true;

    if (!userId || !config.enabled || isLoading) {
      return;
    }

    // Create a unique key for this evaluation to prevent duplicates
    const evalKey = JSON.stringify({
      metrics: engagementMetrics,
      stats: userStats,
      historyLength: promptHistory.length,
    });

    if (evalKey === lastEvaluationRef.current) {
      return;
    }
    lastEvaluationRef.current = evalKey;

    // Only evaluate if we don't have an active prompt
    if (activePrompt) {
      return;
    }

    // Check if there's already a pending manual prompt (handled by the other effect)
    const hasPendingManual = promptHistory.some(
      (p) => p.action === null && p.isManual === true && p.message
    );
    if (hasPendingManual) {
      return;
    }

    // Prevent duplicate evaluations using pendingEvaluationRef
    if (pendingEvaluationRef.current === evalKey) {
      return;
    }
    pendingEvaluationRef.current = evalKey;

    const matchingTrigger = evaluateTriggers(engagementMetrics, userStats, config, promptHistory);

    if (matchingTrigger) {
      // Record the prompt and show it
      recordPromptShown(userId, matchingTrigger)
        .then((promptId) => {
          // Only update state if still mounted and this is still the current evaluation
          if (isMounted && pendingEvaluationRef.current === evalKey) {
            setActivePrompt({
              trigger: matchingTrigger,
              promptId,
            });
          }
        })
        .catch(console.error)
        .finally(() => {
          // Clear pending evaluation when done
          if (pendingEvaluationRef.current === evalKey) {
            pendingEvaluationRef.current = null;
          }
        });
    } else {
      // Clear pending evaluation if no trigger matched
      pendingEvaluationRef.current = null;
    }

    return () => {
      isMounted = false;
    };
  }, [userId, config, engagementMetrics, userStats, promptHistory, activePrompt, isLoading]);

  // Auto-dismiss timer
  useEffect(() => {
    if (!activePrompt || !config.display.autoDismissSeconds || !userId) {
      return;
    }

    const timer = setTimeout(() => {
      // Track locally to prevent re-showing before Firebase updates
      dismissedPromptIdsRef.current.add(activePrompt.promptId);
      recordPromptAction(userId, activePrompt.promptId, 'expired').catch(console.error);
      setActivePrompt(null);
    }, config.display.autoDismissSeconds * 1000);

    return () => clearTimeout(timer);
  }, [activePrompt, config.display.autoDismissSeconds, userId]);

  // Actions
  const dismissPrompt = useCallback(() => {
    if (!activePrompt || !userId) return;

    // Track locally to prevent re-showing before Firebase updates
    dismissedPromptIdsRef.current.add(activePrompt.promptId);

    recordPromptAction(userId, activePrompt.promptId, 'dismissed').catch(console.error);
    setActivePrompt(null);
    setActivePromptRecord(null);
  }, [activePrompt, userId]);

  const clickPrompt = useCallback(() => {
    if (!activePrompt || !userId) return;

    // Track locally to prevent re-showing before Firebase updates
    dismissedPromptIdsRef.current.add(activePrompt.promptId);

    recordPromptAction(userId, activePrompt.promptId, 'clicked').catch(console.error);
    setActivePrompt(null);
    setActivePromptRecord(null);

    // The actual action handling is done by the component that consumes this context
    // based on the effectiveClickAction
  }, [activePrompt, userId]);

  // Compute base effective click action: prompt record override > trigger default > null
  const baseClickAction: DonationClickAction | null = useMemo(() => {
    if (!activePrompt) return null;

    // Priority: prompt record's clickAction > trigger's clickAction > null
    if (activePromptRecord?.clickAction) {
      return activePromptRecord.clickAction;
    }
    if (activePrompt.trigger.clickAction) {
      return activePrompt.trigger.clickAction;
    }
    return null;
  }, [activePrompt, activePromptRecord]);

  // State for resolved template blocks
  const [resolvedTemplateBlocks, setResolvedTemplateBlocks] = useState<DonationBlock[] | null>(
    null
  );

  // Fetch template blocks when needed
  useEffect(() => {
    let isMounted = true;

    if (
      baseClickAction?.type === 'full_message' &&
      baseClickAction.templateId &&
      !baseClickAction.blocks
    ) {
      // Fetch template blocks
      getDonationTemplate(baseClickAction.templateId)
        .then((template) => {
          if (template && isMounted) {
            setResolvedTemplateBlocks(template.blocks);
          }
        })
        .catch(console.error);
    } else {
      if (isMounted) {
        setResolvedTemplateBlocks(null);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [baseClickAction]);

  // Effective click action with resolved template blocks
  const effectiveClickAction: DonationClickAction | null = useMemo(() => {
    if (!baseClickAction) return null;

    // If we have a templateId and resolved blocks, inject them
    if (
      baseClickAction.type === 'full_message' &&
      baseClickAction.templateId &&
      resolvedTemplateBlocks
    ) {
      return {
        ...baseClickAction,
        blocks: resolvedTemplateBlocks,
      };
    }

    return baseClickAction;
  }, [baseClickAction, resolvedTemplateBlocks]);

  // Prepare the active prompt with substituted message - memoized to prevent recalculation
  const preparedPrompt = useMemo(() => {
    if (!activePrompt) return null;
    return {
      ...activePrompt,
      trigger: {
        ...activePrompt.trigger,
        message: substituteMessageVariables(
          activePrompt.trigger.message,
          engagementMetrics,
          userStats
        ),
      },
    };
  }, [activePrompt, engagementMetrics, userStats]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo<DonationContextValue>(
    () => ({
      activePrompt: preparedPrompt,
      dismissPrompt,
      clickPrompt,
      isLoading,
      config,
      effectiveClickAction,
    }),
    [preparedPrompt, dismissPrompt, clickPrompt, isLoading, config, effectiveClickAction]
  );

  return <DonationContext.Provider value={value}>{children}</DonationContext.Provider>;
}

// ============================================
// HOOK
// ============================================

// eslint-disable-next-line react-refresh/only-export-components
export function useDonation(): DonationContextValue {
  const context = useContext(DonationContext);

  if (!context) {
    throw new Error('useDonation must be used within a DonationProvider');
  }

  return context;
}
